import { useEffect, useRef, useState } from "react";

export default function InfiniteCarouselBase({
  slides = [],
  interval = 4500,
  className = "",
}) {
  const total = slides.length;

  const [index, setIndex] = useState(0);       // can reach total (clone)
  const [paused, setPaused] = useState(false);
  const [transition, setTransition] = useState(true);
  const snappingRef = useRef(false);

  const next = () => setIndex((i) => i + 1);

  const prev = () => {
    if (index === 0) return;
    setIndex((i) => i - 1);
  };

  // autoplay
  useEffect(() => {
    if (paused || total <= 1) return;
    const id = setInterval(next, interval);
    return () => clearInterval(id);
  }, [paused, interval, total]);

  // mark snap when reaching clone
  useEffect(() => {
    if (index === total) snappingRef.current = true;
  }, [index, total]);

  const handleTransitionEnd = (e) => {
    if (e.propertyName !== "transform") return;

    if (snappingRef.current) {
      setTransition(false);
      setIndex(0);
      snappingRef.current = false;

      // re-enable transition next paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTransition(true));
      });
    }
  };

  const dotIndex = index >= total ? 0 : index;

  return (
    <section
      className={`infCar ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
    >
      <button className="infNav infPrev" onClick={prev} aria-label="Previous slide">
        ‹
      </button>

      <div className="infViewport">
        <div
          className={`infTrack ${transition ? "infAnimate" : ""}`}
          style={{ transform: `translateX(-${index * 100}%)` }}
          onTransitionEnd={handleTransitionEnd}
        >
          {/* real slides */}
          {slides.map((node, i) => (
            <div className="infSlide" key={i} aria-hidden={dotIndex !== i}>
              {node}
            </div>
          ))}

          {/* clone first */}
          {total > 0 && (
            <div className="infSlide" aria-hidden="true">
              {slides[0]}
            </div>
          )}
        </div>
      </div>

      <button className="infNav infNext" onClick={next} aria-label="Next slide">
        ›
      </button>

      <div className="infDots" role="tablist" aria-label="Carousel pagination">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`infDot ${dotIndex === i ? "active" : ""}`}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-selected={dotIndex === i}
            role="tab"
          />
        ))}
      </div>
    </section>
  );
}
